// Interview Prep page helpers: the Firestore docs, and what the page works out
// from them before and after buildPlan (prepPlanner.ts).
//
//   users/{uid}/prep/settings        PrepSettingsDoc (setup)
//   users/{uid}/prepDays/{day}       { items }: a day as it was first shown (frozen)
//   users/{uid}/prepItems/{itemId}   PrepItemState: what the user did with one item
//   users/{uid}/prepResults/{id}     PrepResult: every solved / hints / missed tap

import type { PrepLevel } from "../data/prep/roleProfiles";
import type { Difficulty, TopicId } from "../data/prep/topics";
import type { PlanItem, PracticeLog, PrepSettings } from "./prepPlanner";
import { addDays, weekday, type PracticeResult, type Rating, type SkillEvent, type Weekday } from "./prepSkills";

export type PrepSettingsDoc = PrepSettings & {
  ratings: Partial<Record<TopicId, Rating>>;
  /** Don't say which topic a problem is from (it gives the approach away) */
  hideTopics?: boolean;
  /** yyyy-mm-dd the setup was finished */
  completedAt: string;
  /** The level from Job search settings the user said no to (levelSuggestion) */
  declinedLevel?: PrepLevel;
};

export type PrepResult = {
  id: string;
  slug: string;
  topic: TopicId;
  difficulty: Difficulty;
  result: PracticeResult;
  minutes?: number;
  /** yyyy-mm-dd */
  day: string;
};

export type PrepItemState = {
  /** The item as planned, so a moved or done one keeps its title and reason */
  item: PlanItem;
  /** The day it was planned for */
  day: string;
  /** skipped: never again; later: "Not now", it comes back in a couple of days */
  status?: "done" | "skipped" | "later";
  /** Dragged to this day */
  movedTo?: string;
  result?: PracticeResult;
  minutes?: number;
  /** Its prepResults doc, removed on undo */
  resultId?: string;
};

export const WEEK: Weekday[] = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
export const WEEKDAY_LABEL: Record<Weekday, string> = {
  mon: "Mon", tue: "Tue", wed: "Wed", thu: "Thu", fri: "Fri", sat: "Sat", sun: "Sun",
};

/** Setup's quick picks; each fills all seven days */
export const TIME_PRESETS: { label: string; days: Record<Weekday, number> }[] = [
  { label: "20 min weekdays", days: { mon: 20, tue: 20, wed: 20, thu: 20, fri: 20, sat: 0, sun: 0 } },
  { label: "30 min weekdays", days: { mon: 30, tue: 30, wed: 30, thu: 30, fri: 30, sat: 0, sun: 0 } },
  { label: "1 h weekdays", days: { mon: 60, tue: 60, wed: 60, thu: 60, fri: 60, sat: 0, sun: 0 } },
  { label: "30 min every day", days: { mon: 30, tue: 30, wed: 30, thu: 30, fri: 30, sat: 30, sun: 30 } },
  { label: "1 h weekdays + 2 h Sat", days: { mon: 60, tue: 60, wed: 60, thu: 60, fri: 60, sat: 120, sun: 0 } },
];
export const DEFAULT_DAY_MINUTES = TIME_PRESETS[1].days;
export const MINUTE_OPTIONS = [0, 15, 20, 30, 45, 60, 90, 120, 180, 240, 300];

/** "45 min", "1 h", "1 h 30" */
export function formatMinutes(minutes: number): string {
  if (minutes < 60) return `${minutes} min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m ? `${h} h ${m}` : `${h} h`;
}

/** "30 min a day · weekends off", "1 h weekdays · 2 h Sat", or "4 h 30 a week" */
export function timeSummary(days: Record<Weekday, number>): string {
  const weekdays = WEEK.slice(0, 5).map((d) => days[d]);
  const weekend = [days.sat, days.sun];
  const same = (list: number[]) => list.every((m) => m === list[0]);
  if (same([...weekdays, ...weekend])) {
    return weekdays[0] ? `${formatMinutes(weekdays[0])} a day` : "No time set";
  }
  if (same(weekdays) && weekdays[0]) {
    const parts = [`${formatMinutes(weekdays[0])} a day`];
    if (!days.sat && !days.sun) parts.push("weekends off");
    else if (days.sat && !days.sun) parts.push(`${formatMinutes(days.sat)} Sat`);
    else if (!days.sat && days.sun) parts.push(`${formatMinutes(days.sun)} Sun`);
    else parts.push(same(weekend) ? `${formatMinutes(days.sat)} weekend days` : "more on weekends");
    return parts.join(" · ");
  }
  return `${formatMinutes(WEEK.reduce((sum, d) => sum + days[d], 0))} a week`;
}

/** New grad when they're looking for new grad or full-time roles and not internships */
export function defaultLevel(jobTypes: unknown): PrepLevel {
  const types = Array.isArray(jobTypes) ? jobTypes.map(String) : [];
  const fullTime = types.some((t) => t === "New grad" || t === "Full-time");
  const intern = types.some((t) => t === "Internship" || t === "Co-op");
  return fullTime && !intern ? "newgrad" : "intern";
}

/**
 * The level to suggest when Job search settings changed since setup: only a
 * clear intern / new grad switch (not both, and never over mid or senior,
 * which only the user picks), and not one they already said no to.
 */
export function levelSuggestion(jobTypes: unknown, settings: Pick<PrepSettingsDoc, "level" | "declinedLevel">): PrepLevel | null {
  const types = Array.isArray(jobTypes) ? jobTypes.map(String) : [];
  const fullTime = types.some((t) => t === "New grad" || t === "Full-time");
  const intern = types.some((t) => t === "Internship" || t === "Co-op");
  if (fullTime === intern || (settings.level !== "intern" && settings.level !== "newgrad")) return null;
  const level = defaultLevel(types);
  return level === settings.level || level === settings.declinedLevel ? null : level;
}

/** The Monday-to-Sunday week holding `day` */
export function weekOf(day: string): string[] {
  const monday = addDays(day, -WEEK.indexOf(weekday(day)));
  return WEEK.map((_, i) => addDays(monday, i));
}

/** "Oct 12 to 18", "Oct 26 to Nov 1" */
export function weekLabel(days: string[]): string {
  const fmt = (day: string, opts: Intl.DateTimeFormatOptions) =>
    new Date(`${day}T12:00:00`).toLocaleDateString("en-US", opts);
  const first = days[0];
  const last = days[days.length - 1];
  const sameMonth = first.slice(0, 7) === last.slice(0, 7);
  return `${fmt(first, { month: "short", day: "numeric" })} to ${fmt(last, sameMonth ? { day: "numeric" } : { month: "short", day: "numeric" })}`;
}

/**
 * Items the planner must keep where the user put them: dragged ones, and ones
 * acted on ahead of their day.
 */
export function pinnedFrom(states: PrepItemState[], today: string): { day: string; item: PlanItem; from?: string }[] {
  return states
    .filter((s) => s.movedTo || (s.status && s.day > today))
    .map((s) => ({ day: s.movedTo ?? s.day, item: s.item, ...(s.movedTo && s.movedTo !== s.day ? { from: s.day } : {}) }));
}

/** The latest tap on each problem, for the planner */
export function practiceLog(results: PrepResult[]): PracticeLog {
  const log: PracticeLog = {};
  for (const r of [...results].sort((a, b) => a.day.localeCompare(b.day))) log[r.slug] = { result: r.result, day: r.day };
  return log;
}

/** What moves skills: practice taps, and stories checked off */
export function skillEvents(results: PrepResult[], states: PrepItemState[]): SkillEvent[] {
  const events: SkillEvent[] = results.map((r) => ({
    kind: "result",
    day: r.day,
    topic: r.topic,
    difficulty: r.difficulty,
    result: r.result,
  }));
  for (const s of states) {
    if (s.status === "done" && s.item.kind === "story" && s.item.topic) {
      events.push({ kind: "story", day: s.movedTo ?? s.day, topic: s.item.topic });
    }
  }
  return events;
}

/**
 * Days in a row, back from today, where every planned item was done. Days with
 * nothing planned don't break it; today only counts once it's finished.
 */
export function streak(
  plan: Record<string, PlanItem[]>,
  states: Map<string, PrepItemState>,
  today: string,
  since: string
): number {
  let count = 0;
  for (let day = today; day >= since; day = addDays(day, -1)) {
    const items = plan[day] ?? [];
    if (!items.length) continue;
    const done = items.every((i) => states.get(i.id)?.status === "done");
    if (done) count++;
    else if (day !== today) break;
  }
  return count;
}

/** A day before today as it was shown, less what was dragged off it, plus what was dragged onto it */
export function shownDay(
  day: string,
  frozen: Record<string, PlanItem[]>,
  pinned: { day: string; item: PlanItem }[]
): PlanItem[] {
  const movedTo = new Map(pinned.map((p) => [p.item.id, p.day]));
  const kept = (frozen[day] ?? []).filter((i) => (movedTo.get(i.id) ?? day) === day);
  const here = pinned.filter((p) => p.day === day && !kept.some((k) => k.id === p.item.id)).map((p) => p.item);
  return [...kept, ...here];
}
